import { useEffect, useRef } from "react";
import { Bell, X } from "lucide-react";
import { useNotifications } from "@/context/NotificationsContext";

// 15 s gives ample time for visual verification while still feeling like a "toast".
const AUTO_DISMISS_MS = 15000;

const ROOT_STYLE = {
  position: "fixed",
  top: "96px",
  right: "24px",
  zIndex: 9999,
  width: "min(26rem, calc(100vw - 48px))",
  pointerEvents: "none",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

/**
 * Always-mounted toast stack. Renders newly-arrived notifications (from polling).
 * Cards auto-dismiss after AUTO_DISMISS_MS or on click.
 *
 * Inline-styled to be immune to any parent CSS (transforms, overflow, opacity).
 */
export default function NotificationsBanner() {
  const { newSinceLastPoll, markRead } = useNotifications();
  const queue = newSinceLastPoll || [];

  // eslint-disable-next-line no-console
  console.log("[Phantasm] banner render, queue length:", queue.length);

  const visible = queue.slice(0, 4);
  const hidden = visible.length === 0;

  return (
    <div
      data-testid="notifications-banner-root"
      style={{ ...ROOT_STYLE, display: hidden ? "none" : "flex" }}
    >
      {visible.map((ev) => (
        <BannerCard key={ev.id} ev={ev} onDismiss={() => markRead(ev.id)} />
      ))}
    </div>
  );
}

const CARD_STYLE = {
  pointerEvents: "auto",
  display: "block",
  width: "100%",
  textAlign: "left",
  border: "1px solid rgba(255,255,255,0.15)",
  background: "rgba(18,18,20,0.95)",
  backdropFilter: "blur(24px)",
  WebkitBackdropFilter: "blur(24px)",
  padding: "16px 20px",
  color: "#fff",
  boxShadow: "0 12px 36px -8px rgba(230,230,230,0.18)",
  cursor: "pointer",
};

function BannerCard({ ev, onDismiss }) {
  const dismissedRef = useRef(false);
  // Pin the dismiss callback to the initial mount so the cleanup
  // can't be retriggered by parent re-renders.
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!dismissedRef.current) {
        dismissedRef.current = true;
        onDismissRef.current?.();
      }
    }, AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, []);

  const handleClick = () => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    onDismissRef.current?.();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="notifications-banner-card"
      data-event-id={ev.id}
      className="animate-fade-in-up"
      style={CARD_STYLE}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
        <Bell
          size={16}
          strokeWidth={1.5}
          style={{ color: "#E6E6E6", marginTop: "2px", flexShrink: 0 }}
        />
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: "10px",
              textTransform: "uppercase",
              letterSpacing: "0.3em",
              color: "#8E9AA6",
              marginBottom: "4px",
            }}
          >
            New update
          </div>
          <div
            data-testid="notifications-banner-message"
            style={{
              fontSize: "14px",
              color: "#fff",
              fontWeight: 300,
              lineHeight: 1.4,
            }}
          >
            {ev.message}
          </div>
        </div>
        <X
          size={14}
          strokeWidth={1.5}
          style={{ color: "#8E9AA6", marginTop: "4px", flexShrink: 0 }}
          aria-hidden
        />
      </div>
    </button>
  );
}
